/**
 * Theme Utilities
 *
 * Helper functions for working with themes
 */

import { getTheme, type ThemeMode } from './theme'

export const applyThemeToDocument = (mode: ThemeMode): void => {
  if (typeof document === 'undefined') return
  const vars = themeToCSSVars(mode)
  const root = document.documentElement
  Object.entries(vars).forEach(([key, value]) => {
    root.style.setProperty(key, value)
  })
  root.setAttribute('data-theme', mode)
  root.classList.remove('light', 'dark')
  root.classList.add(mode)
}

/**
 * Convert theme object to CSS custom properties
 * Useful for applying theme via CSS variables
 */
export const themeToCSSVars = (mode: ThemeMode): Record<string, string> => {
  const theme = getTheme(mode)

  const flatten = (obj: any, prefix = ''): Record<string, string> => {
    const result: Record<string, string> = {}

    for (const key in obj) {
      const value = obj[key]
      const newKey = prefix ? `${prefix}-${key}` : key

      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value)
      ) {
        Object.assign(result, flatten(value, newKey))
      } else {
        result[`--theme-${newKey}`] = String(value)
      }
    }

    return result
  }

  return flatten(theme)
}

/**
 * Get CSS custom properties string for a theme
 */
export const getThemeCSSVars = (mode: ThemeMode): string => {
  const vars = themeToCSSVars(mode)
  return Object.entries(vars)
    .map(([key, value]) => `${key}: ${value};`)
    .join(' ')
}

/**
 * Get a theme value by path
 * Example: getThemeValue('light', 'button.primary.bg')
 */
export const getThemeValue = (
  mode: ThemeMode,
  path: string,
): string | undefined => {
  const theme = getTheme(mode)
  const keys = path.split('.')

  let value: any = theme
  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key as keyof typeof value]
    } else {
      return undefined
    }
  }

  return typeof value === 'string' ? value : undefined
}

/**
 * Check if current theme is dark
 */
export const isDarkTheme = (mode: ThemeMode): boolean => {
  return mode === 'dark'
}

/**
 * Get contrast color (black or white) for a given background color
 */
export const getContrastColor = (bgColor: string): string => {
  // Remove # if present
  const hex = bgColor.replace('#', '')

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

  // Return black or white based on luminance
  return luminance > 0.5 ? '#000000' : '#ffffff'
}

/**
 * Convert hex color to rgba
 */
export const hexToRgba = (hex: string, alpha: number = 1): string => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}
